import React, { ReactNode } from 'react'
import { FaArrowDown, FaArrowUp } from 'react-icons/fa'
import { cx } from '../cx'
import s from './styles.module.scss'

export type StatTone = 'default' | 'accent' | 'good' | 'bad' | 'warn' | 'muted'

export interface StatCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    label: ReactNode
    value: ReactNode
    /** Suffix rendered after the value (%, days). */
    unit?: ReactNode
    /** One-line context under the value. */
    sub?: ReactNode
    /** Signed change; the sign picks the colour. */
    delta?: number | null
    deltaLabel?: ReactNode
    tone?: StatTone
    icon?: ReactNode
    /** Thin coloured bar on top. Pass a token from `colors`; avoid raw hex. */
    accentColor?: string
    compact?: boolean
    /** Highlighted (e.g. active filter). */
    active?: boolean
    /** Format numbers with thousands separators (default true for numeric values). */
    format?: boolean
    /** Let a long label wrap onto two lines instead of ellipsing. */
    wrapLabel?: boolean
}

const nf = new Intl.NumberFormat('en-GB')

export const formatStat = (value: ReactNode): ReactNode => {
    if (typeof value === 'number') return nf.format(value)
    if (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) return nf.format(Number(value))
    return value
}

/** KPI tile: micro-label, big tabular number, optional context line. */
export const StatCard = ({
    label,
    value,
    unit,
    sub,
    delta,
    deltaLabel,
    tone = 'default',
    icon,
    accentColor,
    compact = false,
    active = false,
    format = true,
    wrapLabel = false,
    className,
    onClick,
    style,
    ...rest
}: StatCardProps) => (
    <div
        className={cx(s.card, compact && s.compact, onClick && s.interactive, active && s.active, className)}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        style={accentColor ? { ...style, ['--stat-accent' as any]: accentColor } : style}
        {...rest}
    >
        {accentColor && <span className={s.accentBar} aria-hidden="true" />}
        <div className={s.labelRow}>
            <span className={cx(s.label, wrapLabel && s.labelWrap)}>{label}</span>
            {icon && <span className={s.icon}>{icon}</span>}
        </div>
        <div className={cx(s.value, tone !== 'default' && s[tone])}>
            {format ? formatStat(value) : value}
            {unit && <span className={s.unit}>{unit}</span>}
        </div>
        {(sub || delta != null) && (
            <div className={s.sub}>
                {delta != null && (
                    <span className={cx(s.delta, delta > 0 && s.up, delta < 0 && s.down)}>
                        {delta > 0 ? <FaArrowUp /> : delta < 0 ? <FaArrowDown /> : null}
                        {delta > 0 ? '+' : ''}
                        {nf.format(delta)}
                        {deltaLabel && <> {deltaLabel}</>}
                    </span>
                )}
                {delta != null && sub && ' · '}
                {sub}
            </div>
        )}
    </div>
)

export interface StatGridProps extends React.HTMLAttributes<HTMLDivElement> {
    minColumn?: number
    children: ReactNode
}

export const StatGrid = ({ minColumn = 180, className, style, children, ...rest }: StatGridProps) => (
    <div className={cx(s.grid, className)} style={{ ...style, ['--min-col' as any]: `${minColumn}px` }} {...rest}>
        {children}
    </div>
)
